import { type RefObject, useEffect } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { LoggerService } from '../LoggerService';

interface ResizeObserverEntry {
  target: Element;
  contentRect: DOMRectReadOnly;
}

export type ObserverCallback = (entry: DOMRectReadOnly) => void;

const logger = LoggerService.getLogger().getModuleLogger(useResizeObserver.name);

export function useResizeObserver(
  target: RefObject<Element>,
  callback: ObserverCallback,
  shouldUseParent = false,
): void {
  useEffect(() => {
    const { current } = target;

    // Use ResizeObserver, checking its existence
    const ResizeObserverToUse = window.ResizeObserver || ResizeObserver;
    const resizeObserver = new ResizeObserverToUse((entries: ResizeObserverEntry[]) => {
      // requestAnimationFrame is used to batch multiple resize events into a single update,
      // reducing the number of times the component needs to re-render and improving performance
      requestAnimationFrame(() => {
        if (entries?.[0]) {
          callback(entries[0].contentRect);
        } else {
          logger.error('ResizeObserver entries are undefined.');
        }
      });
    });

    const elementToObserve = shouldUseParent ? current?.parentElement : current;

    if (elementToObserve) {
      resizeObserver.observe(elementToObserve);
    } else {
      logger.error('Element to observe is undefined.');
    }

    return () => {
      if (elementToObserve) resizeObserver.unobserve(elementToObserve);
    };
  }, [callback, shouldUseParent, target]);
}
